// Incomplete Mortage Definitions
import moment from "moment";
import { LoanStatus } from "./loan_statuses";

export default interface MortgageDefinitions {
  LoanStatus: LoanStatus | "–";

  ExternalLoanId: string;
  Borrower1: BorrowerInformation;
  Borrower2?: BorrowerInformation;
  Borrower1CreditScores: CreditScore[];
  Borrower2CreditScores: CreditScore[];

  PurchasedPropertyAddress: Address;
  PurchasedPropertyUsage?: PropertyUsage;
  PurchasedPropertyType?: PropertyType;

  ClosingDate: DateAsString;
  RateLockedAt: TimeAsString;
  LoanAmount: DecimalAsString;
  PurchasePrice: DecimalAsString;
  DownPayment: DecimalAsString;
  NoteRate: DecimalAsString;
  LTV: DecimalAsString;
  DebtToIncomeRatio: DecimalAsString;
  QualifyingMonthlyDebt: DecimalAsString;
  MonthlyIncome: DecimalAsString;
  LoanProgram: string;
  VerifiedAssetsTotal: DecimalAsString;

  EstimatedTotalAssetsRequired: DecimalAsString;
  EstimatedClosingCosts: DecimalAsString;
  EstimatedMinimumReservesRequired: DecimalAsString;
}

export interface CreditScore {
  CreditScore: number;
  // Left out since they're not used but are in the API response
  // RetrievedDate time.Time
  // Bureau        string
  // Factors       []*Factor
}

export const FICOScore = (scores: CreditScore[]) => {
  if (scores.length === 0) {
    return null;
  }
  const scoreValues = scores.map(score => score.CreditScore);
  scoreValues.sort();
  if (scoreValues.length < 3) {
    return scoreValues[0];
  }
  return scoreValues[1];
};

export type TimeAsString = string;
export type DateAsString = string;
export const dateStringAsMoment = (date: DateAsString) => moment(date, "YYYY-MM-DD");

export type DecimalAsString = string;
export const formatRateAsPercent = (num: DecimalAsString, dec = 3) =>
  num !== undefined ? [(Number(num) * 100).toFixed(dec), "%"].join("") : "";

export enum PropertyUsage {
  // Residence that the owner physically occupies and uses as his or her principal residence.
  PrimaryResidence = "PrimaryResidence",
  // A property occupied by the owner for a portion of the year and is not the primary residence.
  SecondHome = "SecondHome",
  Investment = "Investment",
  Other = "Other",
}

export enum PropertyType {
  Unknown = "",
  NotListed = "Not listed",
  // Eave Property Types,
  SFR = "SFR",
  SFRATT = "SFR-ATT",
  PUD = "PUD",
  PUDATT = "PUD-ATT",
  SiteCondo = "Site Condo",
  LowRiseCondo = "Low-Rise Condo",
  MidRiseCondo = "Mid-Rise Condo",
  HighRiseCondo = "High-Rise Condo",
  Duplex = "Duplex",
  DuplexATT = "Duplex-ATT",
  Triplex = "Triplex",
  TriplexATT = "Triplex-ATT",
  Quadplex = "Quadplex",
  QuadplexATT = "Quadplex-ATT",
  // Property Types for which Eave doesn't yet issue loans, but need to handle for owned real estate,
  // TODO (mike) Check with Jane on how detailed we want to get here,
  Cooperative = "Cooperative",
  ManufacturedMobileHome = "ManufacturedMobileHome",
  MultifamilyMoreThanFourUnits = "MultifamilyMoreThanFourUnits",
  Townhouse = "Townhouse",
  CommercialNonResidential = "CommercialNonResidential",
  MixedUseResidential = "MixedUseResidential",
  Farm = "Farm",
  HomeAndBusinessCombined = "HomeAndBusinessCombined",
  Land = "Land",
}

export interface BorrowerInformation {
  FirstName: string;
  MiddleName?: string;
  LastName: string;
  Suffix?: string;
  FullName: string;
  DOB: DateAsString;
  Email: string;
  Phone: string;
  MaritalStatus?: MaritalStatus;
  PresentAddress?: Residence;
  FormerAddress?: Residence[];
}

export enum MaritalStatus {
  Married = "Married",
  NotProvided = "NotProvided",
  Separated = "Separated",
  Unknown = "Unknown",
  Unmarried = "Unmarried",
}

export interface Residence {
  Address: Address;
}

export interface Address {
  AddressLineText: string;
  AddressAdditionalLineText: string;
  CityName: string;
  CountryCode: string;
  CountyCode: string;
  CountyName: string;
  PostalCode: string;
  StateCode: string;
}

export const FormatAddress = (addr: Address) =>
  `${addr.AddressLineText}${addr.AddressAdditionalLineText &&
    ` ${addr.AddressAdditionalLineText}`}${addr.CityName && `, ${addr.CityName}`}${addr.StateCode &&
    `, ${addr.StateCode} `}${addr.PostalCode}`;
